import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useMessagePolling } from '../../../../api/denaliAssistant'
import Card from '../../../../components/molecules/Card'
import { useAppContext } from '../../../../redux/slices/appContext'
import MessageContent from './MessageContent'
import MessageHeader from './MessageHeader'
import MessageFooter from './MessageFooter'

const useStyles = makeStyles((theme) => ({
  message: {
    padding: '0 !important',
    borderRadius: '12px',
    border: '1px solid transparent',
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    fontFamily: 'var(--font-serif)'
  },
  userMessage: {
    marginLeft: '-16px',
    marginRight: '-16px',
    padding: 'var(--dim-4) !important',
    fontFamily: 'var(--font-sans)',
    borderColor: 'var(--dai-color-border1)',
    backgroundColor: 'var(--dai-color-surface3)'
  },
  avatar: {
    width: '20px',
    height: '20px',
    borderRadius: 'var(--border-radius-round)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '18px'
  },
  userAvatar: {
    backgroundColor: 'var(--color-primary-600)',
    color: 'var(--color-contrast-primary-600)'
  },
  agentAvatar: {
    backgroundColor: 'var(--color-ai-500)',
    color: 'var(--color-contrast-ai-500)',
    fill: 'var(--color-contrast-ai-500)',
    stroke: 'var(--color-contrast-ai-500)'
  }
}))

function Message ({ parentInfo, message, hideMessageHeader, onRetry, onSuggestionClick }) {
  const classes = useStyles()
  const isUser = message.actor === 'user'
  const { isSummitUser } = useAppContext()

  // Default to showing explainability during generation, hidden when complete
  const [showExplainability, setShowExplainability] = useState(false)

  // Poll for updates if message is generating
  useMessagePolling(message)

  const handleExplainClick = useCallback((e) => {
    e.stopPropagation()
    e.preventDefault()

    if (isSummitUser) {
      if (e.metaKey) {
        navigator.clipboard.writeText(JSON.stringify(message || {}, null, 2)).catch(console.error)
        return
      }
    }
    setShowExplainability(p => !p)
  }, [setShowExplainability, message, isSummitUser])

  // Only show footer for completed assistant messages
  const showFooter = !isUser && !message.isGenerating && !message.isPlaceholder

  return (
    <Card variant={isUser ? undefined : 'clear'} className={`${classes.message} ${isUser ? classes.userMessage : ''}`}>
      {hideMessageHeader ? null : (
        <MessageHeader
          message={message}
          showExplainability={showExplainability}
          onExplainClick={handleExplainClick}
        />
      )}

      <div className={classes.messageContent}>
        <MessageContent
          parentInfo={parentInfo}
          message={message}
          showExplainability={showExplainability}
          isGenerating={message.isGenerating}
          onSuggestionClick={onSuggestionClick}
        />
        {showFooter && (
          <MessageFooter message={message} onRetry={onRetry} />
        )}
      </div>
    </Card>
  )
}

Message.propTypes = {
  parentInfo: PropTypes.shape({
    parentId: PropTypes.string,
    parentType: PropTypes.oneOf(['schedule', 'thread'])
  }),
  hideMessageHeader: PropTypes.bool,
  message: PropTypes.shape({
    threadMessageId: PropTypes.string,
    actor: PropTypes.oneOf(['user', 'assistan']).isRequired,
    message: PropTypes.string,
    data: PropTypes.object,
    attachments: PropTypes.array,
    isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    generationStatus: PropTypes.string,
    isPlaceholder: PropTypes.bool,
    createdDate: PropTypes.string
  }).isRequired,
  onRetry: PropTypes.func,
  onSuggestionClick: PropTypes.func
}

export default Message
